import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  IconButton,
  useTheme,
  Alert,
  Card,
  CardHeader,
  CardContent,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { useConsultation } from '../hooks/useConsultation';
import type { AppointmentQuestionDto, ConsultationResponseDto } from '../types/consultation';
import PastAppointmentsDialog from './PastAppointmentsDialog';

interface FormData {
  chiefComplaint: string;
  diagnosis: string;
  historyOfPresentIllness: string;
  notes: string;
  questions: AppointmentQuestionDto[];
  additionalConditions: string;
}

interface ConsultationDialogProps {
  open: boolean;
  onClose: () => void;
  appointmentId: string;
  doctorId: string;
  patientId?: string;
  onSaved?: () => void;
}

const healthConditions = [
  'Constipation',
  'Diabetic',
  'High Blood Pressure',
  'Irritable Bowel Syndrome (IBS)',
  'Anxiety',
  'Sleep Disorders',
  'Digestive Issues',
  // Add more conditions as needed
];

const ConsultationDialog: React.FC<ConsultationDialogProps> = ({
  open,
  onClose,
  appointmentId,
  doctorId,
  patientId,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  // Only call useConsultation if we have an appointmentId
  const { 
    consultation, 
    isLoading, 
    error, 
    createConsultation, 
    updateConsultation, 
    isCreating, 
    isUpdating 
  } = useConsultation(appointmentId || '');
  
  // Add a debug effect to log when appointmentId changes
  useEffect(() => {
    console.log('appointmentId changed:', appointmentId);
  }, [appointmentId]);
  
  const [formData, setFormData] = useState<FormData>({
    chiefComplaint: '',
    diagnosis: '',
    historyOfPresentIllness: '',
    notes: '',
    questions: [],
    additionalConditions: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPastAppointmentsOpen, setIsPastAppointmentsOpen] = useState(false);
  
  // Debug effect for past appointments dialog
  useEffect(() => {
    console.log('PastAppointmentsDialog state:', { isPastAppointmentsOpen, patientId, shouldOpen: isPastAppointmentsOpen && !!patientId });
  }, [isPastAppointmentsOpen, patientId]);
  
  // Memoized function to create default questions with the appointmentId
  const createDefaultQuestions = useCallback((apptId: string): AppointmentQuestionDto[] => {
    console.log('Creating default questions for appointment:', apptId);
    
    // Create a question for each health condition
    const questions = healthConditions.map(condition => ({
      question: condition,
      answer: false,
      notes: '',
      appointmentId: apptId
    }));
    
    console.log('Created questions:', questions);
    return questions;
  }, []);

  // Update form data when consultation data is loaded or when opening for a new consultation
  useEffect(() => {
    console.log('useEffect triggered with:', { open, appointmentId, hasConsultation: !!consultation });
    
    // Only proceed if the dialog is open and we have a valid appointmentId
    if (!open || !appointmentId) {
      console.log('Skipping form data initialization - missing open or appointmentId');
      return;
    }
    
    console.log('Initializing form data for appointment:', appointmentId);
    
    const initializeFormData = () => {
      if (consultation) {
        // Editing existing consultation
        console.log('Editing existing consultation:', consultation);
        const questions = consultation.questions?.length > 0 
          ? consultation.questions 
          : createDefaultQuestions(appointmentId);
        
        console.log('Questions for existing consultation:', questions);
        
        // Parse additional conditions from notes (if any)
        let notes = consultation.notes || '';
        let additionalConditions = '';
        
        const additionalConditionsMatch = notes.match(/Additional Conditions:([\s\S]*?)(?=\n\n|$)/);
        if (additionalConditionsMatch) {
          additionalConditions = additionalConditionsMatch[1].trim();
          // Remove the additional conditions from the notes
          notes = notes.replace(/\n?\n?Additional Conditions:[\s\S]*$/, '').trim();
        }
        
        setFormData({
          chiefComplaint: consultation.chiefComplaint || '',
          diagnosis: consultation.diagnosis || '',
          historyOfPresentIllness: consultation.historyOfPresentIllness || '',
          notes: notes,
          questions: Array.isArray(questions) ? questions : [],
          additionalConditions: additionalConditions,
        });
      } else {
        // New consultation
        console.log('Creating new consultation');
        const questions = createDefaultQuestions(appointmentId);
        console.log('Questions for new consultation:', questions);
        
        setFormData({
          chiefComplaint: '',
          diagnosis: '',
          historyOfPresentIllness: '',
          notes: '',
          questions: Array.isArray(questions) ? questions : [],
          additionalConditions: '',
        });
      }
    };
    
    initializeFormData();
    
    // Cleanup function to reset form when dialog is closed
    return () => {
      if (!open) {
        setFormData({
          chiefComplaint: '',
          diagnosis: '',
          historyOfPresentIllness: '',
          notes: '',
          questions: [],
          additionalConditions: '',
        });
      }
    };
  }, [consultation, open, appointmentId]); // Removed createDefaultQuestions from deps since it's now stable

  // Log form data changes
  useEffect(() => {
    console.log('Form data updated:', formData);
    console.log('Questions in form data:', formData.questions);
  }, [formData]);


  const handleQuestionChange = (index: number, field: 'answer' | 'notes', value: any) => {
    console.log('Updating question:', { index, field, value });
    
    setFormData(prev => {
      const updatedQuestions = [...prev.questions];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        [field]: value,
        // Ensure appointmentId is always set
        appointmentId: appointmentId,
      };
      
      console.log('Updated questions:', updatedQuestions);
      
      return {
        ...prev,
        questions: updatedQuestions
      };
    });
  };

  // Handle copying data from past consultation
  const handleCopyFromPast = (pastConsultation: ConsultationResponseDto) => {
    console.log('Copying data from past consultation:', pastConsultation);
    
    setFormData(prev => ({
      ...prev,
      chiefComplaint: pastConsultation.chiefComplaint || '',
      diagnosis: pastConsultation.diagnosis || '',
      historyOfPresentIllness: pastConsultation.historyOfPresentIllness || '',
      notes: pastConsultation.notes || '',
      additionalConditions: pastConsultation.additionalConditions || '',
      // Map past questions to current format with current appointmentId
      questions: pastConsultation.questions?.map(q => ({
        question: q.question,
        answer: q.answer,
        notes: q.notes || '',
        appointmentId: appointmentId, // Use current appointment ID
      })) || prev.questions,
    }));
    
    setIsPastAppointmentsOpen(false);
    enqueueSnackbar('Consultation data copied successfully', { variant: 'success' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Combine the notes and additional conditions into a single notes field
      const combinedNotes = [
        formData.notes,
        formData.additionalConditions && `Additional Conditions: ${formData.additionalConditions}`
      ]
        .filter(Boolean)
        .join('\n\n');
      
      // Prepare questions data - ensure we have the required fields
      const questionsData = formData.questions
        .filter(q => q.question) // Only include questions with text
        .map(q => ({
          question: q.question,
          answer: q.answer || false,
          notes: q.notes || '',
          appointmentId, // Add the appointment ID
          ...(q.id ? { id: q.id } : {}),
        }));

      // Prepare consultation data (only include fields that are allowed by the API)
      const consultationData = {
        chiefComplaint: formData.chiefComplaint,
        diagnosis: formData.diagnosis,
        historyOfPresentIllness: formData.historyOfPresentIllness,
        notes: combinedNotes,
        questions: questionsData, // Include questions in the main payload as required by DTO
        // Don't include additionalConditions as it's not in the DTO
        // Don't include appointmentId and doctorId here as they're part of the URL
      };
      


      if (consultation) {
        // Update existing consultation
        await updateConsultation({
          ...consultationData,
          id: consultation.id,
        });
        
        enqueueSnackbar('Consultation updated successfully', { variant: 'success' });
        onSaved?.();
        onClose();
      } else {
        // Create new consultation
        await createConsultation(consultationData);
        
        enqueueSnackbar('Consultation created successfully', { variant: 'success' });
        onSaved?.();
        onClose();
      }
    } catch (error: any) {
      console.error('Error saving consultation:', error);
      
      // Show error message to user
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save consultation';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

 

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{consultation ? 'Edit Consultation' : 'New Consultation'}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            {error.message || 'Failed to load consultation data'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const isSaving = isCreating || isUpdating;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {consultation ? 'Edit Consultation' : 'New Consultation'}
            </Typography>
            <IconButton onClick={onClose} size="small" disabled={isSaving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
          <Grid item xs={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Please answer the following questions:
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {formData.questions?.map((question, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={question.answer}
                              onChange={(e) => handleQuestionChange(index, 'answer', e.target.checked)}
                              name={`question-${index}`}
                              disabled={isSaving}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {question.question}
                            </Typography>
                          }
                          sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            p: 1,
                            m: 0.5,
                            width: '100%',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  
                  {/* Additional Conditions */}
                  <Box mt={3} width="100%">
                    <Typography variant="subtitle2" gutterBottom>
                      Additional Conditions (not listed above)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="Enter any other conditions not listed above..."
                      value={formData.additionalConditions}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        additionalConditions: e.target.value
                      }))}
                      disabled={isSaving}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
  <Box display="flex" flexDirection="column" gap={2}>
    <TextField
      fullWidth
      label="Chief Complaint"
      name="chiefComplaint"
      value={formData.chiefComplaint}
      onChange={(e) => setFormData(prev => ({ ...prev, chiefComplaint: e.target.value }))}
      required
      multiline
      rows={4}
      variant="outlined"
      disabled={isSaving}
    />
    <TextField
      fullWidth
      label="History of Present Illness"
      name="historyOfPresentIllness"
      value={formData.historyOfPresentIllness}
      onChange={(e) => setFormData(prev => ({ ...prev, historyOfPresentIllness: e.target.value }))}
      required
      multiline
      rows={4}
      variant="outlined"
      disabled={isSaving}
    />
    <TextField
      fullWidth
      label="Diagnosis"
      name="diagnosis"
      value={formData.diagnosis}
      onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
      required
      multiline
      rows={4}
      variant="outlined"
      disabled={isSaving}
    />
    <TextField
      fullWidth
      label="Notes"
      name="notes"
      value={formData.notes}
      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
      multiline
      rows={3}
      variant="outlined"
      disabled={isSaving}
    />
  </Box>
</Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              console.log('Copy from Past clicked, patientId:', patientId);
              setIsPastAppointmentsOpen(true);
            }}
            color="primary"
            variant="outlined"
            startIcon={<HistoryIcon />}
            disabled={isSubmitting}
          >
            Copy from Past
          </Button>
          <Box>
            <Button 
              onClick={onClose} 
              color="primary"
              disabled={isSubmitting}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Saving...' : 'Save Consultation'}
            </Button>
          </Box>
        </DialogActions>
      </form>
      
      {/* Past Appointments Dialog */}
      <PastAppointmentsDialog
        open={isPastAppointmentsOpen && !!patientId}
        onClose={() => setIsPastAppointmentsOpen(false)}
        patientId={patientId || ''}
        currentAppointmentId={appointmentId}
        onCopy={handleCopyFromPast}
      />
    </Dialog>
  );
};

export default ConsultationDialog;
